/**
 * UnifiedDrawer styles
 *
 * Single right-side drawer with one handle and a panel split into two stacked
 * sections (Controls on top, Listings below). Replaces the old CNTLDrawer +
 * ListingsDrawer pair so we don't need cross-drawer height/state coordination.
 */
export const unifiedDrawerStyles = `
/* Wrapper - fixed full-height column on the right. --tabs-height defaults
   to 0px (undefined everywhere except apps/agent, which adds a persistent
   top tab bar below its header - apps/public never sets it, so this stays
   a no-op there). */
.unified-wrapper {
  position: fixed;
  top: calc(var(--header-height, 60px) + var(--tabs-height, 0px) + 2.5vh);
  right: 0;
  bottom: 2.5vh;
  z-index: 750;
  pointer-events: none;
  font-family: inherit;
}

/* Form controls don't inherit font-family in Chrome/Safari — force it */
.unified-wrapper input,
.unified-wrapper button,
.unified-wrapper label,
.unified-wrapper select {
  font-family: inherit;
}

/* Handle - vertical strip on the right edge, vertically centered on the map
   viewport (midpoint between header bottom and viewport bottom). Fixed rather
   than absolute so it stays centered regardless of wrapper inset. */
.unified-handle {
  position: fixed;
  right: 0;
  top: calc(50vh + (var(--header-height, 60px) + var(--tabs-height, 0px)) / 2);
  transform: translateY(-50%);
  width: 40px;
  background: rgba(255, 255, 255, 0.98);
  padding: 10px 6px;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow:
    -3px 0 8px rgba(22, 163, 74, 0.3),
    -6px 0 20px rgba(22, 163, 74, 0.15);
  transition: padding-left 0.2s ease, box-shadow 0.2s ease, border-left-color 0.2s ease, right 0.3s ease;
  border-left: 2px solid rgba(22, 163, 74, 0.5);
  pointer-events: auto;
  z-index: 10;
}

/* While the user is dragging the resize grip, kill the width/right transitions
   so the handle and panel move together with the cursor instead of easing. */
.unified-wrapper.resizing .unified-panel,
.unified-wrapper.resizing .unified-handle {
  transition: none !important;
}

.unified-handle:hover {
  padding-left: 10px;
  box-shadow:
    -4px 0 12px rgba(22, 163, 74, 0.4),
    -8px 0 25px rgba(22, 163, 74, 0.2);
  border-left-color: rgba(22, 163, 74, 0.7);
}

/* Stacked status indicator dots at the top of the handle */
.unified-handle-dots {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.unified-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid transparent;
  box-sizing: border-box;
  transition: background 0.15s, border-color 0.15s;
}

.unified-dot.for-sale { border-color: #22c55e; }
.unified-dot.for-sale.active { background: #22c55e; }
.unified-dot.pending { border-color: #f59e0b; }
.unified-dot.pending.active { background: #f59e0b; }
.unified-dot.recently-sold { border-color: #93c5fd; }
.unified-dot.recently-sold.active { background: #93c5fd; }
.unified-dot.off-market { border-color: #9ca3af; }
.unified-dot.off-market.active { background: #9ca3af; }

/* Points left (toward the map/center) when closed, flips to point right
   (toward the panel that's now open beside it) when open - the transition
   is what makes the flip itself readable as a state change, not just the
   end position. */
.unified-handle-arrow {
  flex-shrink: 0;
  transform: rotate(90deg);
  transition: transform 0.2s ease;
}

.unified-wrapper.open .unified-handle-arrow {
  transform: rotate(270deg);
}

/* "Listings" rotated text label */
.unified-handle-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 0.75rem;
  font-weight: 500;
  color: #333;
  letter-spacing: 0.5px;
  user-select: none;
}

/* Attribute icons at the bottom of the handle (only shown when active) */
.unified-handle-icons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  min-height: 0;
}

/* Color comes from the shared attribute registry via an inline style, not a
   per-attribute class here (see renderHandleIcons). */
.unified-handle-icon {
  width: 12px;
  height: 12px;
}

/* Panel - slides in from right, fills full height of wrapper */
.unified-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 0 12px;
  display: flex;
  flex-direction: column;
  width: 0;
  overflow: hidden;
  opacity: 0;
  transition: width 0.3s ease, opacity 0.2s ease;
}

.unified-wrapper.open .unified-panel {
  opacity: 1;
  pointer-events: auto;
}

/* When open, push the handle left by the panel width (set inline via JS) */
.unified-wrapper.open .unified-handle {
  /* handle right-offset is set inline */
}

/* Resize grip on the left edge of the panel — always visible (unlike old version) */
.unified-resize {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.unified-resize-grip {
  width: 4px;
  height: 48px;
  background: #16a34a;
  border-radius: 2px;
  opacity: 0.55;
  box-shadow: 0 0 6px rgba(22, 163, 74, 0.35);
  transition: opacity 0.2s ease, background 0.2s ease, height 0.2s ease;
  position: relative;
}

/* Three subtle horizontal grooves so the grip clearly reads as "draggable" */
.unified-resize-grip::before,
.unified-resize-grip::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}
.unified-resize-grip::before { top: 30%; }
.unified-resize-grip::after { bottom: 30%; }

.unified-resize:hover .unified-resize-grip {
  opacity: 1;
  background: #15803d;
  height: 64px;
  box-shadow: 0 0 12px rgba(22, 163, 74, 0.7);
}

.unified-resize.dragging .unified-resize-grip {
  opacity: 1;
  background: #15803d;
}

/* Body wraps the two stacked sections; padded so resize grip stays clear */
.unified-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-left: 8px; /* reserve space for resize grip */
}

/* Top section — Controls. Fixed height, tight padding. */
.unified-section-controls {
  flex-shrink: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #fafafa;
}

/* Bottom section — Listings. Fills remaining space and scrolls. */
.unified-section-listings {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

/* Phase 1 placeholder content — remove once real sections are mounted */
.unified-placeholder {
  padding: 16px;
  color: #6b7280;
  font-size: 13px;
  font-style: italic;
}

/* Mobile (<768px) */
@media (max-width: 767px) {
  .unified-handle {
    width: 36px;
    padding: 8px 4px;
  }
  .unified-handle-label {
    font-size: 0.7rem;
  }
  .unified-resize {
    /* No drag-resize on mobile — touch hit area is too small to be useful */
    display: none;
  }
  .unified-body {
    padding-left: 0;
  }
}

/* Touch device tweaks */
@media (hover: none) and (pointer: coarse) {
  .unified-handle {
    width: 44px;
  }
}
`;
export function injectUnifiedDrawerStyles() {
    if (document.getElementById('unified-drawer-styles'))
        return;
    const style = document.createElement('style');
    style.id = 'unified-drawer-styles';
    style.textContent = unifiedDrawerStyles;
    document.head.appendChild(style);
}
//# sourceMappingURL=styles.js.map